import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isNonEmptyString} from 'ramda-adjunct';

import {
  MazdaClaimScopeSchema,
  MazdaClaimWarrantyTypeSchema,
  PutServiceOrderIntegrationsMazdaClaimApiArg,
} from '@dms/api/workshop';

import {Nullish} from 'shared/web';

import {
  MAZDA_CLAIM_MAINTENANCE_DONE_RIGHT,
  MAZDA_CLAIM_MAINTENANCE_NOT_DONE_CORRECTLY,
  MazdaClaimSendFormType,
} from '../types/MazdaClaimSendFormType';
import {hasMazdaWarrantyInstallationDetails} from './hasMazdaWarrantyInstallationDetails';

type MazdaClaimRequestBody = PutServiceOrderIntegrationsMazdaClaimApiArg['body'];

// Save and close submits raw form values without running validation, so every field may be missing.
type MazdaClaimSendFormValues = Partial<MazdaClaimSendFormType>;

const toNullableString = (value: string | Nullish) => {
  const trimmedValue = value?.trim();

  return isNonEmptyString(trimmedValue) ? trimmedValue : null;
};

// The response can report the TSC scope, which the save contract does not accept.
const toScope = (value: string | Nullish) => {
  const result = MazdaClaimScopeSchema.safeParse(value);

  return result.success ? result.data : null;
};

const toWarrantyType = (value: string | Nullish) => {
  const result = MazdaClaimWarrantyTypeSchema.safeParse(value);

  return result.success ? result.data : null;
};

const toMaintenanceFlag = (value: string | Nullish) =>
  match<string | Nullish, boolean | null>(value)
    .with(MAZDA_CLAIM_MAINTENANCE_DONE_RIGHT, always(true))
    .with(MAZDA_CLAIM_MAINTENANCE_NOT_DONE_CORRECTLY, always(false))
    .otherwise(always(null));

export const mapMazdaClaimFormToRequestBody = (
  data: MazdaClaimSendFormValues
): MazdaClaimRequestBody => {
  const hasInstallationDetails = hasMazdaWarrantyInstallationDetails(data.warrantyType);

  return {
    scope: toScope(data.scope),
    warrantyType: toWarrantyType(data.warrantyType),
    mileage: data.mileage ?? null,
    repairDate: toNullableString(data.repairDate),
    vehicleServicedAccordingToManufacturerSpecs: toMaintenanceFlag(
      data.vehicleServicedAccordingToManufacturerSpecs
    ),
    installationDate: hasInstallationDetails ? toNullableString(data.installationDate) : null,
    installationMileage: hasInstallationDetails ? (data.installationMileage ?? null) : null,
    installationRepairNo: hasInstallationDetails
      ? toNullableString(data.installationRepairNo)
      : null,
    causalPart: data.isSpecialCausalPart ? null : toNullableString(data.causalPart),
    specialCausalPart: data.isSpecialCausalPart ? toNullableString(data.specialCausalPart) : null,
    message: toNullableString(data.message),
  };
};
